import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Users, Inbox, Phone, CheckCircle2, Save, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite, useBrand } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";

const STATUSES = ["All", "New", "Contacted", "Site Visit", "Closed"];
const CONFIG_FIELDS = [
  ["phone", "Phone Number"], ["whatsapp", "WhatsApp Number"], ["email", "Email"],
  ["address", "Address"], ["gst", "GST Number"], ["business_hours", "Business Hours"],
  ["lead_notify_email", "Lead Notification Email"],
  ["facebook", "Facebook URL"], ["instagram", "Instagram URL"], ["linkedin", "LinkedIn URL"], ["youtube", "YouTube URL"],
];

export default function Admin() {
  const { user, logout } = useAuth();
  const { config, setConfig } = useSite();
  const brand = useBrand();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("All");
  const [cfgForm, setCfgForm] = useState({});
  const [cfgMsg, setCfgMsg] = useState("");
  const [cfgErr, setCfgErr] = useState("");
  const [tab, setTab] = useState("leads");
  useSeo({ title: `Admin Dashboard | ${brand.name}` });

  useEffect(() => { if (user === false) nav("/login"); }, [user, nav]);
  useEffect(() => { if (config) setCfgForm(config); }, [config]);

  const load = useCallback(async () => {
    const [l, s] = await Promise.all([
      api.get(`/admin/leads${filter !== "All" ? `?status=${encodeURIComponent(filter)}` : ""}`),
      api.get("/admin/stats"),
    ]);
    setLeads(l.data); setStats(s.data);
  }, [filter]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    load();
  };
  const del = async (id) => { await api.delete(`/admin/leads/${id}`); load(); };
  const saveConfig = async () => {
    // Previously had no catch at all: a failed save (expired login session,
    // network error, validation rejection) threw an unhandled promise
    // rejection — nothing was shown on screen, no "Saved!", no error, so it
    // looked like the button silently did nothing. Now any failure is shown
    // in the same spot the success message used to occupy alone, and an
    // expired/invalid session sends the admin back to /login instead of
    // leaving them stuck clicking a button that can never succeed.
    setCfgMsg(""); setCfgErr("");
    try {
      const { data } = await api.put("/admin/config", cfgForm);
      setConfig(data.config);
      setCfgMsg("Saved!"); setTimeout(() => setCfgMsg(""), 2000);
    } catch (e) {
      if (e?.response?.status === 401) {
        nav("/login");
        return;
      }
      setCfgErr(formatApiError(e?.response?.data?.detail) || "Save failed — please try again.");
    }
  };

  if (!user) return <div className="min-h-screen bg-[#060B11] flex items-center justify-center text-slate-400">Loading...</div>;

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="card-surface p-5"><Icon className={color} size={22} /><div className="text-3xl font-display font-bold text-white mt-2">{value ?? 0}</div><div className="text-xs text-slate-400 mt-1">{label}</div></div>
  );

  return (
    <div className="min-h-screen bg-[#060B11]">
      <header className="border-b border-white/10 bg-[#0B1522] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1 flex items-center"><img src="/logo.jpeg" alt="HSP logo" className="h-9 w-auto" /></div>
            <div><div className="font-display font-bold text-white">HSP Admin</div><div className="text-xs text-slate-500">{user.email}</div></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-ghost !py-2 !px-3" data-testid="admin-refresh"><RefreshCw size={16} /></button>
            <button onClick={() => { logout(); nav("/"); }} className="btn-ghost !py-2 !px-4 text-sm" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Inbox} label="Total Leads" value={stats.total} color="text-emerald-400" />
          <StatCard icon={Users} label="New" value={stats.new} color="text-amber-400" />
          <StatCard icon={Phone} label="Contacted" value={stats.contacted} color="text-cyan-400" />
          <StatCard icon={CheckCircle2} label="Closed" value={stats.closed} color="text-emerald-400" />
        </div>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("leads")} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "leads" ? "bg-emerald-500 text-[#04261C]" : "bg-[#0B1522] text-slate-300 border border-white/10"}`} data-testid="admin-tab-leads">Leads</button>
          <button onClick={() => setTab("config")} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "config" ? "bg-emerald-500 text-[#04261C]" : "bg-[#0B1522] text-slate-300 border border-white/10"}`} data-testid="admin-tab-config">Business Config</button>
        </div>

        {tab === "leads" ? (
          <>
            <div className="flex gap-2 mb-4 flex-wrap" data-testid="admin-status-filter">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider ${filter === s ? "bg-emerald-500 text-[#04261C]" : "bg-[#0B1522] text-slate-400 border border-white/10"}`} data-testid={`admin-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</button>
              ))}
            </div>
            <div className="card-surface overflow-x-auto" data-testid="admin-leads-table">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500 border-b border-white/10">
                  <th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Requirement</th><th className="p-4">Source</th><th className="p-4">Status</th><th className="p-4"></th>
                </tr></thead>
                <tbody>
                  {leads.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No leads yet.</td></tr>}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-lead-row-${l.id}`}>
                      <td className="p-4"><div className="text-white font-medium">{l.name}</div><div className="text-xs text-slate-500">{l.city} {l.company && `· ${l.company}`}</div></td>
                      <td className="p-4 text-slate-300"><div>{l.mobile}</div><div className="text-xs text-slate-500">{l.email}</div></td>
                      <td className="p-4 text-slate-300">{l.requirement || "—"}<div className="text-xs text-slate-500">{l.capacity}</div></td>
                      <td className="p-4 text-xs text-slate-500">{l.source}</td>
                      <td className="p-4">
                        <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`admin-lead-status-${l.id}`} className="bg-[#060B11] border border-white/10 rounded px-2 py-1 text-xs text-slate-200">
                          {STATUSES.slice(1).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-4"><button onClick={() => del(l.id)} className="text-red-400 hover:text-red-300" data-testid={`admin-lead-delete-${l.id}`}><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="card-surface p-6 max-w-2xl">
            <h2 className="font-display text-xl font-bold text-white mb-1">Business Configuration</h2>
            <p className="text-slate-400 text-sm mb-6">These values power the header, footer, contact page, WhatsApp/Call buttons and lead notification email.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {CONFIG_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs text-slate-500 mb-1 block">{label}</label>
                  <input value={cfgForm[k] || ""} onChange={(e) => setCfgForm({ ...cfgForm, [k]: e.target.value })} data-testid={`admin-config-${k}`} className="w-full bg-[#060B11] border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 focus:border-emerald-500 outline-none" />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 mt-6">
              <button onClick={saveConfig} className="btn-primary" data-testid="admin-config-save"><Save size={16} /> Save Configuration</button>
              {cfgMsg && <span className="text-emerald-400 text-sm" data-testid="admin-config-msg">{cfgMsg}</span>}
              {cfgErr && <span className="text-red-400 text-sm" data-testid="admin-config-error">{cfgErr}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
